import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Archive, Download, FileArchive, CheckCircle2, Circle, HardDriveDownload, Send, CalendarPlus, Eye, Trash2, FlaskConical, Lock } from "lucide-react";
import { api, mediaUrl, errMsg, triggerDownload } from "@/lib/galleryApi";
import { formatBytes } from "@/components/gallery/PurgeDialog";
import { fmtDate } from "@/components/gallery/retentionUi";
import { RestoreDialog } from "@/components/gallery/RestoreDialog";

const CHECKS = [["generated", "Archive generee"], ["downloaded", "Toutes les parties telechargees"], ["verified", "Integrite verifiee (archive reimportee)"],
  ["restore_tested", "Test de restauration reussi"], ["local_confirmed", "Sauvegarde locale confirmee"]];

function PartsDialog({ data, onClose }) {
  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0B1120] border-[#1A2332] text-white" data-testid="archive-parts-dialog">
        <DialogHeader>
          <DialogTitle>Archive complete — {data?.links.length} partie(s)</DialogTitle>
          <DialogDescription className="text-[#94A3B8]">Originaux et retouches en qualite d'origine (aucune recompression), configuration et manifeste d'empreintes. Chaque partie est restaurable seule. Liens valables 6 h.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {data?.links.map((l) => (
            <Button key={l.part} onClick={() => triggerDownload(mediaUrl(l.url))} className="w-full justify-between h-11 bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid={`archive-part-${l.part}`}>
              <span>Partie {l.part} — {l.count} fichiers · {formatBytes(l.bytes)}</span><Download className="w-4 h-4" />
            </Button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LocalDialog({ archive, onClose, onSaved }) {
  const [ok, setOk] = useState(false);
  const [loc, setLoc] = useState("");
  const save = async () => {
    try { await api.post(`/gallery-archives/${archive.id}/confirm-local`, { confirm: true, location: loc }); toast.success("Sauvegarde locale confirmee"); onSaved(); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!archive} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0B1120] border-[#1A2332] text-white" data-testid="local-backup-dialog">
        <DialogHeader><DialogTitle>Confirmer la sauvegarde locale</DialogTitle>
          <DialogDescription className="text-[#94A3B8]">Le telechargement seul ne prouve pas la sauvegarde. Confirmez que toutes les parties sont copiees sur votre serveur ou NAS.</DialogDescription></DialogHeader>
        <Input value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="Emplacement (ex. NAS bureau /Reportages/2026)" className="bg-[#060B14] border-[#1A2332] text-white" data-testid="local-backup-location" />
        <label className="flex items-start gap-2 text-sm text-[#94A3B8]"><Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} data-testid="local-backup-checkbox" />J'atteste que les {archive?.parts.length} partie(s) de cette archive sont sauvegardees localement.</label>
        <Button disabled={!ok || !loc.trim()} onClick={save} className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="local-backup-confirm">Confirmer</Button>
      </DialogContent>
    </Dialog>
  );
}

function ArchiveCard({ a, onLinks, onLocal }) {
  return (
    <div className="bg-[#0B1120] border border-[#1A2332] rounded-xl p-5" data-testid={`archive-${a.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <p className="text-sm font-semibold">Archive du {fmtDate(a.created_at)}</p>
          <p className="text-xs text-[#64748B] mt-1">{a.photo_count} photos · {a.file_count} fichiers · {formatBytes(a.total_bytes)} · {a.parts.length} partie(s) · format v{a.version} · par {a.created_by_name}</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => onLinks(a)} className="bg-transparent border-[#1A2332] text-[#94A3B8]" data-testid={`archive-redownload-${a.id}`}><Download className="w-3.5 h-3.5 mr-1.5" />Telecharger</Button>
          {!a.local_backup && <Button size="sm" onClick={() => onLocal(a)} disabled={!a.checks.downloaded} className="bg-[#10B981]/15 text-[#10B981] hover:bg-[#10B981]/25" data-testid={`archive-confirm-local-${a.id}`}><HardDriveDownload className="w-3.5 h-3.5 mr-1.5" />Confirmer la sauvegarde locale</Button>}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
        {CHECKS.map(([k, label]) => (
          <div key={k} className={`flex items-center gap-2 text-xs rounded-lg px-3 py-2 border ${a.checks[k] ? "border-[#10B981]/30 text-[#10B981]" : "border-[#1A2332] text-[#64748B]"}`} data-testid={`archive-check-${k}`}>
            {a.checks[k] ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" /> : <Circle className="w-3.5 h-3.5 shrink-0" />}{label}
          </div>
        ))}
      </div>
      <div className="mt-3 text-[11px] text-[#64748B] space-y-1">
        {a.parts.map((p) => <p key={p.part}>Partie {p.part} : {formatBytes(p.bytes)} · {p.downloaded_at ? `telechargee le ${fmtDate(p.downloaded_at)}` : "non telechargee"} · {p.verified_at ? `verifiee le ${fmtDate(p.verified_at)}` : "non verifiee"} · test {p.test_ok == null ? "non realise" : p.test_ok ? "reussi" : "en echec"}</p>)}
        {a.local_backup && <p className="text-[#10B981]">Sauvegarde locale confirmee le {fmtDate(a.local_backup.at)} par {a.local_backup.by_name} — {a.local_backup.location}</p>}
        <p className="flex items-center gap-1.5"><Lock className="w-3 h-3" />Suppression des fichiers en ligne : desactivee{a.deletion_ready ? " — tous les controles sont valides, activation possible apres votre accord" : ""}</p>
      </div>
    </div>
  );
}

function Reactivation({ gallery, pendingIds, onPublished, onSend, onExtend }) {
  const [checked, setChecked] = useState(false);
  const publish = async () => {
    try { const r = await api.post(`/galleries/${gallery.id}/publish`, { photo_ids: pendingIds }); toast.success(`${r.published_count} photo(s) republiee(s)`); onPublished(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-[#F97316]/5 border border-[#F97316]/30 rounded-xl p-5" data-testid="reactivation-panel">
      <h3 className="text-sm font-semibold mb-1">Galerie restauree — reactivation controlee</h3>
      <p className="text-xs text-[#94A3B8] mb-3">Restauree le {fmtDate(gallery.restored_at)}. Les anciens liens ne sont pas reactives{gallery.restored_access_mode && gallery.restored_access_mode !== "private" ? ` (mode d'origine : ${gallery.restored_access_mode}${gallery.restored_had_code ? ", code a redefinir" : ""})` : ""}.</p>
      {pendingIds.length > 0 && (
        <label className="flex items-start gap-2 text-xs text-[#94A3B8] mb-3"><Checkbox checked={checked} onCheckedChange={(v) => setChecked(!!v)} data-testid="reactivation-check" />J'ai verifie les {pendingIds.length} photo(s) a republier et les autorisations de diffusion.</label>
      )}
      <div className="flex flex-wrap gap-2">
        {pendingIds.length > 0 && <Button size="sm" disabled={!checked} onClick={publish} className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="reactivation-publish"><Eye className="w-3.5 h-3.5 mr-1.5" />Republier {pendingIds.length} photo(s)</Button>}
        <Button size="sm" onClick={onSend} className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="reactivation-send"><Send className="w-3.5 h-3.5 mr-1.5" />Renvoyer le lien au client</Button>
        <Button size="sm" variant="outline" onClick={onExtend} className="bg-transparent border-[#1A2332] text-white" data-testid="reactivation-extend"><CalendarPlus className="w-3.5 h-3.5 mr-1.5" />Prolonger l'acces de 7 jours</Button>
      </div>
    </div>
  );
}

export function ArchivePanel({ gallery, photos, onPhotosChanged, onSend }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [parts, setParts] = useState(null);
  const [local, setLocal] = useState(null);
  const [restore, setRestore] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/galleries/${gallery.id}/archives`).then(setData).catch((e) => toast.error(errMsg(e))), [gallery.id]);
  useEffect(() => { load(); }, [load]);
  const pendingIds = photos.filter((p) => p.restore_pending_publish && !p.in_trash).map((p) => p.id);

  const create = async () => {
    setBusy(true);
    try { const r = await api.post(`/galleries/${gallery.id}/archives`, {}); setParts(r); load(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const links = async (a) => { try { setParts(await api.post(`/gallery-archives/${a.id}/links`, {})); } catch (e) { toast.error(errMsg(e)); } };
  const extend = async () => { try { await api.post(`/galleries/${gallery.id}/retention`, { action: "extend" }); toast.success("Acces prolonge de 7 jours"); } catch (e) { toast.error(errMsg(e)); } };
  const dropTest = async () => {
    if (!window.confirm("Supprimer la galerie de test et ses fichiers temporaires ? L'archive et la galerie d'origine ne sont pas touchees.")) return;
    try { await api.del(`/galleries/${gallery.id}`); toast.success("Galerie de test supprimee"); navigate("/app/galeries"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5" data-testid="archive-panel">
      {gallery.is_restore_test && (
        <div className="flex flex-wrap items-center justify-between gap-3 bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 rounded-xl px-5 py-4" data-testid="restore-test-banner">
          <p className="text-sm flex items-center gap-2"><FlaskConical className="w-4 h-4 text-[#A78BFA]" />Galerie de test de restauration — privee, invisible des clients.</p>
          <Button size="sm" variant="outline" onClick={dropTest} className="bg-transparent border-red-500/40 text-red-400" data-testid="delete-test-gallery"><Trash2 className="w-3.5 h-3.5 mr-1.5" />Supprimer la galerie de test</Button>
        </div>
      )}
      {gallery.restored_at && !gallery.is_restore_test && <Reactivation gallery={gallery} pendingIds={pendingIds} onPublished={onPhotosChanged} onSend={onSend} onExtend={extend} />}
      <div className="flex flex-wrap gap-3">
        <Button onClick={create} disabled={busy} className="bg-white text-black hover:bg-white/90" data-testid="create-archive-btn"><Archive className="w-4 h-4 mr-2" />{busy ? "Preparation…" : "Telecharger l'archive complete"}</Button>
        <Button onClick={() => setRestore(true)} variant="outline" className="bg-transparent border-[#F97316]/40 text-[#F97316]" data-testid="open-restore-btn"><FileArchive className="w-4 h-4 mr-2" />Restaurer depuis une archive ZIP</Button>
      </div>
      <h3 className="text-sm font-semibold pt-2">Sauvegardes et restaurations</h3>
      {data && data.archives.length === 0 && <p className="text-sm text-[#64748B]" data-testid="archives-empty">Aucune archive pour cette galerie.</p>}
      {data?.archives.map((a) => <ArchiveCard key={a.id} a={a} onLinks={links} onLocal={setLocal} />)}
      {data?.restores.length > 0 && (
        <div className="bg-[#0B1120] border border-[#1A2332] rounded-xl p-5" data-testid="restores-history">
          <p className="text-xs uppercase tracking-wider text-[#64748B] mb-2">Restaurations vers cette galerie</p>
          {data.restores.map((r) => <p key={r.at} className="text-xs text-[#94A3B8] py-1">{fmtDate(r.at)} · {r.mode === "test" ? "test" : "restauration"} partie {r.part} · {r.restored} restauree(s), {r.skipped} deja presente(s) · {r.by_name}{r.test_ok != null ? ` · ${r.test_ok ? "test reussi" : "test en echec"}` : ""}</p>)}
        </div>
      )}
      <PartsDialog data={parts} onClose={() => { setParts(null); setTimeout(load, 1500); }} />
      <LocalDialog archive={local} onClose={() => setLocal(null)} onSaved={load} />
      <RestoreDialog open={restore} onOpenChange={setRestore} galleryId={gallery.id} onDone={() => { load(); onPhotosChanged(); }} />
    </div>
  );
}
